import { Box, Flex, Text } from "rebass/styled-components";
import { createContext, useEffect, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { useRecoilState, useRecoilValue } from "recoil";

import { BaseSection, Container } from "./styledComponents";
import { TitleAndLoadouts, NoLoadout } from "./TitleAndLoadouts";
import { Moment } from "./Moment";
import { Button } from "../Button";
import { TLoadout } from "MVVM/Views/CreateMontage/@types";
import { FinalTouches } from "./FinalTouches";
import { QuickEditViewModel } from "./ViewModel";
import { LoadingSpinner } from "../Lottie";
import theme from "theme/themeThree";
import {
  StyledIcon,
  StyledRemixButton,
} from "MVVM/Views/Studio/RemixView/Components/Pinned";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredThumbnail,
  QuickEditTitle,
} from "MVVM/State/quickEdit";

import {
  getMatchHistoryGameEnum,
  getPlayerGameIdByGame,
} from "MVVM/Views/MatchHistory/utils";
import { ESupportedGames } from "MVVM/@types";
import { useUser } from "MVVM/Hooks/useUser";
import { analytics } from "MVVM/Analytics";
import { canUserCreateCareerMontage } from "MVVM/Utilities/Montage";
import { IQuickEditMoment } from "./@types";
import {
  ProfileMontageSession,
  defaultState,
} from "MVVM/State/profileMontages";
import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { EPaywallType } from "MVVM/Components/Modals/Paywall";

import { ActiveAdAtom } from "MVVM/State/ads";
import { FinalizingContainer } from "./FinalizingContainer";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { SubscriptionPaywall } from "MVVM/Components/Modals/SubscriptionPaywall";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { canUserCreateCareerMontage as canUserCreateMontage } from "MVVM/Utilities/Montage";
import { AdUnit } from "MVVM/Components/Ads";
import { useMediaQuery } from "MVVM/Hooks/useMediaQuery";
import { isAtClipLimit } from "MVVM/Utilities/ClipLimits";
import { addPendingClipCreation } from "MVVM/Utilities/ClipLimits/pendingClipCreations";
import { ClipQuotaBar } from "MVVM/Components/ClipQuotaBar";
import { TimeRemap, ITimeRemapParams } from "./TimeRemap";

// Context for rendering tooltips outside the scrollable area
export const TooltipPortalContext = createContext<HTMLDivElement | null>(null);

const TOTAL_STEPS = 2;

export type TQuickEditType = "MOMENT" | "MONTAGE";
export type TQuickEditFlow = "standard" | "time-remap";
interface IQuickEditProps {
  type?: TQuickEditType;
  momentId: string;
  closeModal: () => void;
  onCreate?: () => void;
}

export const QuickEdit = ({
  momentId,
  closeModal,
  onCreate,
  type = "MOMENT",
}: IQuickEditProps) => {
  const { allstarUser } = useUser();
  const [step, setStep] = useState<number>(1);
  const [flow, setFlow] = useState<TQuickEditFlow>("standard");
  const [adRefreshIndex, setAdRefreshIndex] = useState(0);
  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const [selectedLoadout, setSelectedLoadout] = useState<TLoadout | null>(
    preferredLoadout || NoLoadout,
  );
  const [activeAd] = useRecoilState(ActiveAdAtom);
  const isMatchHistory = window.location.href.includes("match-history");
  const showSideAds = useMediaQuery("(min-width: 1200px)");
  const showBillboard = useMediaQuery("(min-width: 1000px)");
  const [thumbnailKey, setThumbnailKey] = useState<string>("");
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [title, setTitle] = useRecoilState(QuickEditTitle);
  const [finalizing, setFinalizing] = useState<boolean>(false);
  const [loadingAd] = useState<boolean>(false);
  const isCreatingRef = useRef(false);
  const tooltipPortalRef = useRef<HTMLDivElement>(null);

  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      onClose={() => setEligibilityModalOpen(false)}
      closeQuickEditModal={closeModal}
    />,
  );

  const showSubscriptionOverhaul = useShouldShowSubscriptionOverhaul();
  const isPremiumDisabled = showSubscriptionOverhaul
    ? allstarUser?.user?.userGroup?.type === "FREE"
    : allstarUser?.user?.userGroup?.type === "FREE" ||
      allstarUser?.user?.userGroup?.type === "ESSENTIALS";
  const allFeaturesLocked = showSubscriptionOverhaul && isPremiumDisabled;
  const [paywallFeatureName, setPaywallFeatureName] = useState<string>("");

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <SubscriptionPaywall
      featureName={paywallFeatureName}
      requiredTierKey="ESSENTIALS"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const openPaywall = (featureName: string) => {
    analytics.track("Upgrade CTA Clicked", {
      location: `Quick Edit - ${featureName}`,
    });
    setPaywallFeatureName(featureName);
    setSubscriptionPaywallOpen(true);
  };

  const [selectedFinalTouches, setSelectedFinalTouches] = useState<string[]>(
    [],
  );
  const [isOwnMoment, setIsOwnMoment] = useState<boolean>(false);

  const userGroup = allstarUser?.user?.userGroup?.type;
  const canCreateCareerMontage =
    type === "MONTAGE"
      ? showSubscriptionOverhaul
        ? canUserCreateMontage(userGroup)
        : canUserCreateCareerMontage(userGroup)
      : false;
  const {
    moment,
    momentCreateClip,
    momentCreateMontage,
    mediaUploadUrl,
    loadouts,
    campaignAlert,
  } = QuickEditViewModel(momentId);
  const [montageSession, setMontageSession] = useRecoilState(
    ProfileMontageSession,
  );
  const [, setAllstarModalState] = useRecoilState(AllstarModalState);
  const onComplete = useCallback(() => {
    setTitle("");
    closeModal();
  }, [setTitle, closeModal]);

  useEffect(() => {
    const relevantPlayerGameId = getPlayerGameIdByGame(
      getMatchHistoryGameEnum(moment?.game),
      allstarUser,
    );
    setIsOwnMoment(relevantPlayerGameId === moment?.player?.gameId);
  }, [moment, allstarUser]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (finalizing) {
      timeout = setTimeout(() => {
        onComplete();
      }, 5000);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [finalizing, onComplete]);

  useEffect(() => {
    analytics.track("Quick Edit Display", { type });
  }, [type]);

  const effectsOverridesGetter = (loadoutId?: string) => {
    return {
      thumbnail: thumbnailKey ?? preferredThumbnail,
      loadout: loadoutId,
      rapid: selectedFinalTouches.includes("rapidFire"),
      contextCameras: selectedFinalTouches.includes("contextCams"),
      applySignatureMusic: selectedFinalTouches.includes("music"),
      applySignatureCards: selectedFinalTouches.includes("signature"),
      disableJumpCuts: selectedFinalTouches.includes("disableJumpCuts"),
      applyMountedCameras: selectedFinalTouches.includes("mountedCameras"),
      applyFinalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      creatorCards: [
        ...(selectedFinalTouches.includes("hideWatermark")
          ? ["60a6d86a8c4fd555132afc97"]
          : []),
        ...(selectedFinalTouches.includes("hideKillFeed")
          ? ["622be8b4ff38f13a9d241322"]
          : []),
      ],
      ...(title && { title }),
    };
  };

  const handleCreateMontage = async (loadoutId?: string) => {
    if (!canUserCreateMontage(userGroup)) return false;
    analytics.track("Montage Requested", {
      version: 2,
      clips: montageSession.clips?.length,
    });

    analytics.track("Montage Quick Edit Create", {
      game: moment?.game,
      thumbnailUploaded: !!thumbnailKey,
      loadoutSelected: !!selectedLoadout?._id,
      signatureEnabled: selectedFinalTouches.includes("signature"),
      musicEnabled: selectedFinalTouches.includes("music"),
      rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
      contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
      jumpCuts: !selectedFinalTouches.includes("disableJumpCuts"),
      mountedCameras: selectedFinalTouches.includes("mountedCameras"),
      finalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      titleEdited: title !== "",
    });
    setFinalizing(true);
    await momentCreateMontage({
      variables: {
        MomentsCreateMontageInput: {
          ids: montageSession.clips.map((clip) => clip.moment!.id),
          effectsOverrides: effectsOverridesGetter(loadoutId),
        },
      },
    });
    setMontageSession(defaultState);

    return true;
  };

  const handleCreateClip = async (
    moment: IQuickEditMoment,
    loadoutId?: string,
  ): Promise<boolean> => {
    const result = await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [momentId],
          matchId: moment.match.id,
          effectsOverrides: effectsOverridesGetter(loadoutId),
          checkEligibility: showSubscriptionOverhaul,
        },
      },
    });

    const errors = result?.data?.momentCreateClip?.errors;
    const eligibilityError = errors?.find(
      (e: any) => e.field === "eligibility",
    );
    if (eligibilityError) {
      setEligibilityModalOpen(true);
      isCreatingRef.current = false;
      return false;
    }

    addPendingClipCreation();

    analytics.track("Quick Edit Create", {
      thumbnailUploaded: !!thumbnailKey,
      loadoutSelected: !!selectedLoadout?._id,
      signatureEnabled: selectedFinalTouches.includes("signature"),
      musicEnabled: selectedFinalTouches.includes("music"),
      rapidFireEnabled: selectedFinalTouches.includes("rapidFire"),
      contextCamsEnabled: selectedFinalTouches.includes("contextCams"),
      jumpCuts: !selectedFinalTouches.includes("disableJumpCuts"),
      mountedCameras: selectedFinalTouches.includes("mountedCameras"),
      finalKillCamera: selectedFinalTouches.includes("finalKillCamera"),
      titleEdited: title !== "",
    });

    setFinalizing(true);
    return true;
  };

  const handleCreateTimeRemap = async (params: ITimeRemapParams) => {
    if (!moment) return;

    analytics.track("Time Remap Create", {
      applyStudioCards: params.applyStudioCards,
      hasSong: !!params.selectedSong,
      impactSeverity: params.impactSeverity,
      songVolume: params.songVolume,
      speedupVfxEnabled: params.speedupVfxEnabled,
      titleEdited: params.title !== "",
    });

    const result = await momentCreateClip({
      variables: {
        momentCreateClipInput: {
          ids: [momentId],
          matchId: moment.match.id,
          effectsOverrides: {
            ...(params.title && { title: params.title }),
          },
          timeRemapParams: {
            applyStudioCards: params.applyStudioCards,
            remapConfig: {
              severity: params.impactSeverity,
            },
            ...(params.selectedSong && {
              amsCard: params.selectedSong._id,
            }),
            speedupVfxSelection: params.speedupVfxEnabled ? "flash" : null,
          },
        },
      },
    });

    const errors = result?.data?.momentCreateClip?.errors;
    const eligibilityError = errors?.find(
      (e: any) => e.field === "eligibility",
    );
    if (eligibilityError) {
      setEligibilityModalOpen(true);
      return;
    }

    addPendingClipCreation();

    setFinalizing(true);
  };

  const handleClick = async (moment: IQuickEditMoment) => {
    if (!loadouts) return;
    if (step === TOTAL_STEPS) {
      if (isCreatingRef.current) return;
      isCreatingRef.current = true;

      if (type === "MOMENT" && allstarUser?.user?._id) {
        if (
          showSubscriptionOverhaul &&
          isAtClipLimit(allstarUser?.user?.clipQuota)
        ) {
          setEligibilityModalOpen(true);
          isCreatingRef.current = false;
          return;
        }
      }

      let loadoutId = selectedLoadout?._id;
      if (selectedLoadout?.name === "Surprise Me") {
        const randomLoadout =
          loadouts[Math.floor(Math.random() * loadouts.length)];
        loadoutId = randomLoadout._id;
      }
      if (selectedLoadout?.name === "No Loadout") {
        loadoutId = "NO_LOADOUT";
      }

      if (type === "MOMENT") {
        const success = await handleCreateClip(moment, loadoutId);
        if (!success) return;
        if (!isOwnMoment && isMatchHistory) closeModal();
      } else {
        const success = await handleCreateMontage(loadoutId);
        if (!success) {
          isCreatingRef.current = false;
          return;
        }
      }

      setPreferredThumbnail(thumbnailKey);

      onCreate?.();
    } else {
      analytics.track("Quick Edit Next", { type });
      setStep(step + 1);
      setAdRefreshIndex((prev) => prev + 1);
    }
  };

  if (!moment)
    return (
      <Container
        alignItems={"center"}
        justifyContent={"center"}
        minHeight={650}
      >
        <LoadingSpinner height={25} width={25} />
      </Container>
    );
  if (finalizing || loadingAd || activeAd === "clip-modal") {
    return (
      <FinalizingContainer
        finalizing={finalizing}
        loadingAd={loadingAd}
        activeAd={activeAd}
        closeModal={closeModal}
      />
    );
  }

  // Time Remap flow - separate UI
  if (flow === "time-remap") {
    return (
      <>
        <Container minHeight={300} flexDirection={"column"}>
          <TimeRemap
            moment={moment}
            onBack={() => setFlow("standard")}
            onCreateTimeRemap={handleCreateTimeRemap}
            title={title}
            setTitle={setTitle}
            closeModal={closeModal}
            allFeaturesLocked={allFeaturesLocked}
          />
          {eligibilityModalOpen && EligibilityModalPortal}
        </Container>
      </>
    );
  }

  return (
    <>
      <Container minHeight={300} flexDirection={"column"}>
        <BaseSection
          flexDirection={"column"}
          style={{ gap: 12, padding: "24px" }}
          justifyContent={"center"}
          alignItems={"start"}
        >
          <Text variant="text.headingXSmall">
            Customize Your {type === "MOMENT" ? "Clip" : "Montage"}:{" "}
          </Text>
          {type === "MOMENT" && <Moment moment={moment} />}

          {campaignAlert && (
            <Text
              variant="text.labelXSmall"
              color={theme.colors.error}
              style={{
                fontWeight: "normal",
              }}
            >
              {campaignAlert}
            </Text>
          )}
          {type === "MOMENT" && (
            <ClipQuotaBar
              clipQuota={allstarUser?.user?.clipQuota}
              userEmail={allstarUser?.decoded?.sub}
              compact
            />
          )}
        </BaseSection>
        <TooltipPortalContext.Provider value={tooltipPortalRef.current}>
          <ScrollableSection>
            {step === 1 && (
              <TitleAndLoadouts
                moment={moment}
                selectedLoadout={selectedLoadout}
                setSelectedLoadout={setSelectedLoadout}
                setTitle={setTitle}
                title={title}
                loadouts={loadouts ?? []}
                type={type}
                onTimeRemapClick={
                  getMatchHistoryGameEnum(moment?.game) === ESupportedGames.CS2
                    ? () => {
                        analytics.track("Time Remap Flow Started");
                        setFlow("time-remap");
                      }
                    : undefined
                }
                onCustomLoadoutClick={
                  allFeaturesLocked
                    ? () => openPaywall("Custom Loadout")
                    : undefined
                }
                onLoadoutChange={
                  allFeaturesLocked
                    ? () => openPaywall("Loadout Selection")
                    : undefined
                }
              />
            )}
            {step === 2 && (
              <BaseSection
                flexDirection={"column"}
                style={{ padding: "24px", width: "100%" }}
                justifyContent={"center"}
                alignItems={"start"}
              >
                <FinalTouches
                  setThumbnailKey={setThumbnailKey}
                  selectedFinalTouches={selectedFinalTouches}
                  setSelectedFinalTouches={setSelectedFinalTouches}
                  mediaUploadUrl={mediaUploadUrl}
                  isOwnMoment={isOwnMoment}
                  moment={moment}
                  closeModal={closeModal}
                />
              </BaseSection>
            )}
          </ScrollableSection>
          <TooltipPortalTarget ref={tooltipPortalRef} />
        </TooltipPortalContext.Provider>
        <Controls>
          <Gradient currentStep={step} totalSteps={TOTAL_STEPS} />
          <Button
            classification="roundedFilledAlt"
            size="small"
            onClick={() => closeModal()}
            flex={true}
          >
            Cancel
          </Button>
          <Flex flexDirection={"row"} style={{ gap: 12 }}>
            {step > 1 && (
              <Button
                classification="roundedFilledAlt"
                size="small"
                onClick={() => setStep(step - 1)}
                flex={true}
              >
                Back
              </Button>
            )}

            {step === TOTAL_STEPS ? (
              <>
                {type === "MONTAGE" &&
                !canCreateCareerMontage &&
                showSubscriptionOverhaul ? (
                  <Button
                    flex={true}
                    size="tiny"
                    classification="roundedFilled"
                    onClick={() => openPaywall("Career Montage")}
                    py={5}
                  >
                    Create
                  </Button>
                ) : type === "MONTAGE" && !canCreateCareerMontage ? (
                  <AnimatedTooltip
                    content="Career Montages require Pro tier or higher"
                    position="bottom"
                  >
                    <UpgradeButton
                      onClick={() => {
                        analytics.track("Upgrade CTA Clicked", {
                          location: "Quick Edit - Career Montage",
                        });
                        closeModal();
                        setAllstarModalState({
                          data: {
                            type: EPaywallType.signature as EPaywallType,
                          },
                          isOpen: ModalType.Paywall,
                          functions: {},
                        });
                      }}
                    >
                      Upgrade to Pro
                    </UpgradeButton>
                  </AnimatedTooltip>
                ) : type === "MONTAGE" ? (
                  <Button
                    flex={true}
                    size="tiny"
                    classification="roundedFilled"
                    disabled={finalizing || loadingAd}
                    onClick={() => handleClick(moment)}
                    py={5}
                  >
                    Create
                  </Button>
                ) : moment?.clip?.id ? (
                  <StyledRemixButton
                    size="small"
                    disabled={finalizing || loadingAd}
                    onClick={() => handleClick(moment)}
                    classification="remixFeed"
                  >
                    <StyledIcon />
                    Remix
                  </StyledRemixButton>
                ) : (
                  <Button
                    classification="roundedFilled"
                    size="small"
                    disabled={finalizing || loadingAd}
                    onClick={() => handleClick(moment)}
                    flex={true}
                  >
                    Create
                  </Button>
                )}
              </>
            ) : (
              <Button
                classification="roundedFilled"
                size="small"
                disabled={finalizing}
                onClick={() => handleClick(moment)}
                flex={true}
              >
                Next
              </Button>
            )}
          </Flex>
        </Controls>
        {eligibilityModalOpen && EligibilityModalPortal}
        {subscriptionPaywallOpen && SubscriptionPaywallPortal}
      </Container>
      {showSideAds && (
        <>
          <SideAdOverlay $position="left">
            <AdUnit
              key={`quick-edit-left-${adRefreshIndex}`}
              slotId="quick-edit-left-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              showUpgradeCta
            />
          </SideAdOverlay>
          <SideAdOverlay $position="right">
            <AdUnit
              key={`quick-edit-right-${adRefreshIndex}`}
              slotId="quick-edit-right-skyscraper"
              adUnitType="large-skyscraper"
              collapseIfEmpty
              showUpgradeCta
            />
          </SideAdOverlay>
        </>
      )}
    </>
  );
};

const ScrollableSection = styled.div`
  width: 100%;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
`;

const TooltipPortalTarget = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 100;

  & > * {
    pointer-events: auto;
  }
`;

const Gradient = styled(Box)<{ currentStep: number; totalSteps: number }>`
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  background: ${({ currentStep, totalSteps }) =>
    currentStep === totalSteps
      ? `linear-gradient(to right, ${theme.colors.envy} 50%, ${theme.colors.chalkAlpha20} 50%)`
      : theme.colors.chalkAlpha20};
`;

const Controls = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  max-height: 100px;
  padding: 24px;
  position: relative;
`;

const UpgradeButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.spectrum};
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 34px;
  justify-content: center;
  padding: 0 24px;
  transition: opacity 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const SideAdOverlay = styled.div<{ $position: "left" | "right" }>`
  position: fixed;
  top: 36%;
  ${({ $position }) => ($position === "left" ? "left: 24px;" : "right: 24px;")}
  transform: translateY(-50%);
  width: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  pointer-events: auto;

  @media (min-width: 1600px) {
    ${({ $position }) =>
      $position === "left"
        ? "left: calc(50% - 250px - 300px - 150px);"
        : "right: calc(50% - 250px - 300px - 150px);"}
  }
`;

const BillboardOverlay = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0px;
  display: flex;
  justify-content: center;
  z-index: 100;
  pointer-events: auto;
`;
